import { useEffect } from "react";
import { ArrowRight, Camera, FolderUp, Gauge } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SuiteLayout, GradientText, NeonButton, GhostButton } from "../../components/suite/SuiteLayout";
import { PricingCards } from "../../components/suite/PricingCards";
import { TrialStepper, AddonBanner, FaqSection } from "../../components/suite/HomeSections";
import { usePlan } from "../../lib/plans";

const Section = ({ id, kicker, title, children }) => (
  <section id={id} className="max-w-7xl mx-auto px-4 sm:px-6 pt-24" data-testid={`home-section-${id}`}>
    <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400">{kicker}</p>
    <h2 className="mt-3 mb-10 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{title}</h2>
    {children}
  </section>
);

export default function SuiteHome() {
  const nav = useNavigate();
  const { plan } = usePlan();
  const toPricing = () => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  useEffect(() => {
    if (window.location.hash === "#pricing") setTimeout(toPricing, 150);
  }, []);
  return (
    <SuiteLayout credits={plan?.available} title="Try Risk-Free">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center" data-testid="home-hero">
        <div className="suite-rise">
          <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-400/30 bg-cyan-400/10 font-mono text-[11px] tracking-[0.2em] text-cyan-300">
            7-DAY FREE TRIAL · 10 CREDITS · NO CHARGE TODAY
          </p>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]" data-testid="home-hero-title">
            Try AI Image Suites Risk-Free – <GradientText>Choose Your Plan</GradientText>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">
            Batch-rename, tag and add photo details (EXIF) to hundreds of images in one go. Or capture straight from your camera, drone or studio folder. Every file costs 1 credit, and you always see the total before you start.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <NeonButton onClick={toPricing} data-testid="home-hero-cta">Start free trial <ArrowRight size={18} /></NeonButton>
            <GhostButton onClick={() => nav("/batch")} data-testid="home-hero-batch">Open Batch Processing</GhostButton>
          </div>
        </div>
        <div className="suite-rise grid gap-4" style={{ animationDelay: "120ms" }}>
          {[[FolderUp, "Batch Upload", "Drop up to 500 files at once", "text-cyan-300"], [Gauge, "Speed queues", "Basic · Fast · Priority", "text-purple-300"], [Camera, "Capture suite", "Camera, Watcher, Aerial survey", "text-emerald-300"]].map(([Icon, t, d, c]) => (
            <div key={t} className="rounded-2xl border border-white/10 bg-slate-950/60 backdrop-blur-xl p-5 flex items-center gap-4 hover:border-cyan-400/40 transition-colors">
              <Icon className={c} size={26} />
              <div><p className="font-display font-bold text-white">{t}</p><p className="text-sm text-slate-400">{d}</p></div>
            </div>
          ))}
        </div>
      </section>

      <Section id="trial" kicker="How it works" title="How the Free Trial Works"><TrialStepper /></Section>
      <Section id="pricing" kicker="Pricing" title="Pick the plan that fits your volume">
        <PricingCards current={plan && ["trialing", "active"].includes(plan.status) ? plan.tier : null} trialEligible={plan ? plan.trial_eligible : true} />
        <div className="mt-10"><AddonBanner /></div>
      </Section>
      <Section id="faq" kicker="FAQ" title="Credits, billing and cancelling"><FaqSection /></Section>
    </SuiteLayout>
  );
}
